import { useEffect } from "react";

type SEOConfig = {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  keywords?: string;
  jsonLd?: object | object[];
};

/** Primary production domain — used for absolute canonical & OG URLs. */
export const SITE_URL = "https://neurowellnesstms.com";

const DEFAULT_OG_IMAGE =
  "https://assets.cdn.filesafe.space/k94aHcRKI8Yx46SYfgfo/media/6a888227b647fb6eca258f39.png";

/** Convert a relative path (e.g. "/about") to an absolute URL for canonical/OG tags.
 *  Strips trailing slashes so the canonical exactly matches the served route. */
function toAbsoluteUrl(path?: string): string {
  if (!path || path === "/") return `${SITE_URL}/`;
  if (/^https?:\/\//i.test(path)) {
    const noTrailing = path.replace(/\/+$/, "");
    return noTrailing || `${path.replace(/^(https?:\/\/[^/]+).*/, "$1")}/`;
  }
  const cleanPath = path.replace(/\/+$/, "") || "/";
  return (
    SITE_URL.replace(/\/$/, "") +
    (cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`)
  );
}

function upsertMeta(
  name: string,
  content: string,
  attr: "name" | "property" = "name",
) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${name}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id: string, data: object | object[]) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * Per-page SEO hook. Updates document title, meta description,
 * canonical link, Open Graph / Twitter tags, and optional JSON-LD.
 */
export function useSEO({
  title,
  description,
  canonical,
  ogImage,
  keywords,
  jsonLd,
}: SEOConfig) {
  useEffect(() => {
    const fullTitle = title;
    document.title = fullTitle;

    upsertMeta("description", description);
    upsertMeta("author", "Neuro Wellness TMS Centers of America");
    if (keywords) {
      upsertMeta("keywords", keywords);
    }

    const canonicalHref = toAbsoluteUrl(canonical || "/");
    upsertLink("canonical", canonicalHref);

    const image = ogImage || DEFAULT_OG_IMAGE;

    // Open Graph
    upsertMeta("og:title", fullTitle, "property");
    upsertMeta("og:description", description, "property");
    upsertMeta("og:type", "website", "property");
    upsertMeta("og:url", canonicalHref, "property");
    upsertMeta("og:image", image, "property");
    upsertMeta(
      "og:site_name",
      "Neuro Wellness TMS Centers of America",
      "property",
    );

    // Twitter
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", fullTitle);
    upsertMeta("twitter:description", description);
    upsertMeta("twitter:image", image);

    // JSON-LD
    if (jsonLd) {
      upsertJsonLd("page-jsonld", jsonLd);
    } else {
      const existing = document.getElementById("page-jsonld");
      if (existing) existing.remove();
    }
  }, [title, description, canonical, ogImage, keywords, jsonLd]);
}
